import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { THEME_FONT_OPTIONS, type ThemeFontId, resolveThemeFontId } from '@/theme/fontCatalog';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthData } from '@/hooks/getAuthData';
import { usePatchBusinessUnitThemeFont } from '@/hooks/api/useBusinessUnitBranding';

export const BusinessUnitThemeFontSelect: React.FC = () => {
  const { user } = useAuth();
  const { roles } = useAuthData();
  const isAdmin = roles.some((r) => r.name === 'Admin' || r.name === 'SystemAdmin');
  const bu = user?.currentBusinessUnit;
  const buId = bu?.id;
  const keyFromServer = bu?.themeFontKey;

  const patchMutation = usePatchBusinessUnitThemeFont();
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState<ThemeFontId>(resolveThemeFontId(keyFromServer));

  useEffect(() => {
    setValue(resolveThemeFontId(keyFromServer));
  }, [keyFromServer]);

  if (!isAdmin || buId == null) {
    return null;
  }

  const handleSave = async () => {
    try {
      await patchMutation.mutateAsync({ businessUnitId: buId, themeFontKey: value });
      enqueueSnackbar('Business unit font updated', { variant: 'success' });
    } catch (e: unknown) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Failed to save font', { variant: 'error' });
    }
  };

  const dirty = resolveThemeFontId(keyFromServer) !== value;

  return (
    <Box sx={{ maxWidth: 420 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Business unit font
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Applies across the app for users working in the current business unit. Technical or monospace-style fields may
        still use a fixed font.
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="bu-font-label">Font</InputLabel>
        <Select
          labelId="bu-font-label"
          label="Font"
          value={value}
          onChange={(e) => setValue(e.target.value as ThemeFontId)}
        >
          {THEME_FONT_OPTIONS.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button variant="contained" onClick={() => void handleSave()} disabled={!dirty || patchMutation.isPending}>
        {patchMutation.isPending ? <CircularProgress size={22} /> : 'Save font'}
      </Button>
    </Box>
  );
};
